import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { getEventSessionRegistrationInfo } from "@/modules/eventRegistration/features/EventRegistration/api/getEventSessionRegistrationInfo/getEventSessionRegistrationInfoApi";
import type { EventSessionRegistrationInfoData } from "@/modules/eventRegistration/features/EventRegistration/api/getEventSessionRegistrationInfo/types";

// ---------- Types ----------

export interface Attendee {
  firstName: string;
  lastName: string;
  email?: string;
  isSelected: boolean;
  isWaitlist: boolean;
  specialRequests?: string;
}

interface RegistrationState {
  currentStep: number;
  eventInfo: EventSessionRegistrationInfoData | null;
  eventSessionUuid: string;
  // Contact
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  groupNotes: string;
  // Attendees
  attendees: Attendee[];
  // Discounts
  voucherQuantity: number;
  discountCode: string | null;
  discountValueType: "percentage" | "fixed_amount" | null;
  discountRawValue: number;
  adminDiscountType: "percentage" | "fixed_amount" | null;
  adminDiscountValue: number;
  adminDiscountReason: string;
  // UI
  isLoading: boolean;
  error: string | null;
}

type RegistrationAction =
  | { type: "SET_STEP"; step: number }
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "SET_EVENT_INFO"; info: EventSessionRegistrationInfoData }
  | {
      type: "UPDATE_CONTACT";
      data: Partial<
        Pick<
          RegistrationState,
          "contactName" | "contactEmail" | "contactPhone" | "groupNotes"
        >
      >;
    }
  | { type: "SET_ATTENDEES"; attendees: Attendee[] }
  | { type: "APPLY_VOUCHER"; quantity: number }
  | { type: "REMOVE_VOUCHER" }
  | {
      type: "APPLY_DISCOUNT_CODE";
      code: string;
      valueType: "percentage" | "fixed_amount";
      value: number;
    }
  | { type: "REMOVE_DISCOUNT_CODE" }
  | {
      type: "APPLY_ADMIN_DISCOUNT";
      discountType: "percentage" | "fixed_amount";
      value: number;
      reason: string;
    }
  | { type: "REMOVE_ADMIN_DISCOUNT" }
  | { type: "UPDATE_AVAILABLE_SEATS"; seats: number }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "RESET_FLOW" };

interface RegistrationContextType extends RegistrationState {
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  updateContact: (
    data: Partial<
      Pick<
        RegistrationState,
        "contactName" | "contactEmail" | "contactPhone" | "groupNotes"
      >
    >,
  ) => void;
  setAttendees: (attendees: Attendee[]) => void;
  updateAvailableSeats: (seats: number) => void;
  applyVoucher: (quantity: number) => void;
  removeVoucher: () => void;
  applyDiscountCode: (code: string, valueType: "percentage" | "fixed_amount", value: number) => void;
  removeDiscountCode: () => void;
  applyAdminDiscount: (
    discountType: "percentage" | "fixed_amount",
    value: number,
    reason: string,
  ) => void;
  removeAdminDiscount: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  resetFlow: () => void;
}

// ---------- Initial State ----------

function createInitialState(eventSessionUuid: string): RegistrationState {
  return {
    currentStep: 0,
    eventInfo: null,
    eventSessionUuid,
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    groupNotes: "",
    attendees: [],
    voucherQuantity: 0,
    discountCode: null,
    discountValueType: null,
    discountRawValue: 0,
    adminDiscountType: null,
    adminDiscountValue: 0,
    adminDiscountReason: "",
    isLoading: true,
    error: null,
  };
}

// ---------- Reducer ----------

const MAX_STEP = 1;

function registrationReducer(
  state: RegistrationState,
  action: RegistrationAction,
): RegistrationState {
  switch (action.type) {
    case "SET_STEP":
      return { ...state, currentStep: Math.max(0, Math.min(action.step, MAX_STEP)) };
    case "NEXT_STEP":
      return { ...state, currentStep: Math.min(state.currentStep + 1, MAX_STEP) };
    case "PREV_STEP":
      return { ...state, currentStep: Math.max(state.currentStep - 1, 0) };
    case "SET_EVENT_INFO":
      return { ...state, eventInfo: action.info };
    case "UPDATE_CONTACT":
      return { ...state, ...action.data };
    case "SET_ATTENDEES":
      return { ...state, attendees: action.attendees };
    case "APPLY_VOUCHER":
      return { ...state, voucherQuantity: action.quantity };
    case "REMOVE_VOUCHER":
      return { ...state, voucherQuantity: 0 };
    case "APPLY_DISCOUNT_CODE":
      return {
        ...state,
        discountCode: action.code,
        discountValueType: action.valueType,
        discountRawValue: action.value,
      };
    case "REMOVE_DISCOUNT_CODE":
      return { ...state, discountCode: null, discountValueType: null, discountRawValue: 0 };
    case "APPLY_ADMIN_DISCOUNT":
      return {
        ...state,
        adminDiscountType: action.discountType,
        adminDiscountValue: action.value,
        adminDiscountReason: action.reason,
      };
    case "REMOVE_ADMIN_DISCOUNT":
      return {
        ...state,
        adminDiscountType: null,
        adminDiscountValue: 0,
        adminDiscountReason: "",
      };
    case "UPDATE_AVAILABLE_SEATS":
      return {
        ...state,
        eventInfo: state.eventInfo
          ? { ...state.eventInfo, availableSeats: action.seats }
          : state.eventInfo,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.loading };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "RESET_FLOW":
      return createInitialState(state.eventSessionUuid);
    default:
      return state;
  }
}

// ---------- Context ----------

const RegistrationContext = createContext<RegistrationContextType | null>(null);

// ---------- Provider ----------

interface RegistrationProviderProps {
  eventSessionUuid: string;
  children: React.ReactNode;
}

export function RegistrationProvider({
  eventSessionUuid,
  children,
}: RegistrationProviderProps) {
  const [state, dispatch] = useReducer(
    registrationReducer,
    eventSessionUuid,
    createInitialState,
  );

  // Fetch event info on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchEventInfo() {
      dispatch({ type: "SET_LOADING", loading: true });
      dispatch({ type: "SET_ERROR", error: null });

      try {
        const response =
          await getEventSessionRegistrationInfo(eventSessionUuid);
        if (cancelled) return;

        const info = response.data;
        dispatch({ type: "SET_EVENT_INFO", info });

        // Pre-fill contact info from employee defaults
        if (info.defaultContactName || info.defaultContactEmail) {
          dispatch({
            type: "UPDATE_CONTACT",
            data: {
              contactName: info.defaultContactName || "",
              contactEmail: info.defaultContactEmail || "",
            },
          });
        }

        // Pre-fill default attendee (logged-in user)
        if (info.defaultAttendee) {
          dispatch({
            type: "SET_ATTENDEES",
            attendees: [
              {
                firstName: info.defaultAttendee.firstName,
                lastName: info.defaultAttendee.lastName,
                email: info.defaultAttendee.email,
                isSelected: true,
                isWaitlist: false,
                specialRequests: info.defaultAttendee.specialRequests || undefined,
              },
            ],
          });
        }
      } catch {
        if (!cancelled) {
          dispatch({
            type: "SET_ERROR",
            error: "Failed to load event information. Please try again.",
          });
        }
      } finally {
        if (!cancelled) {
          dispatch({ type: "SET_LOADING", loading: false });
        }
      }
    }

    fetchEventInfo();
    return () => {
      cancelled = true;
    };
  }, [eventSessionUuid]);

  // Action creators
  const nextStep = useCallback(() => dispatch({ type: "NEXT_STEP" }), []);
  const prevStep = useCallback(() => dispatch({ type: "PREV_STEP" }), []);
  const goToStep = useCallback(
    (step: number) => dispatch({ type: "SET_STEP", step }),
    [],
  );
  const updateContact = useCallback(
    (
      data: Partial<
        Pick<
          RegistrationState,
          "contactName" | "contactEmail" | "contactPhone" | "groupNotes"
        >
      >,
    ) => dispatch({ type: "UPDATE_CONTACT", data }),
    [],
  );
  const setAttendees = useCallback(
    (attendees: Attendee[]) => dispatch({ type: "SET_ATTENDEES", attendees }),
    [],
  );
  const updateAvailableSeats = useCallback(
    (seats: number) => dispatch({ type: "UPDATE_AVAILABLE_SEATS", seats }),
    [],
  );
  const applyVoucher = useCallback(
    (quantity: number) => dispatch({ type: "APPLY_VOUCHER", quantity }),
    [],
  );
  const removeVoucher = useCallback(
    () => dispatch({ type: "REMOVE_VOUCHER" }),
    [],
  );
  const applyDiscountCode = useCallback(
    (code: string, valueType: "percentage" | "fixed_amount", value: number) =>
      dispatch({ type: "APPLY_DISCOUNT_CODE", code, valueType, value }),
    [],
  );
  const removeDiscountCode = useCallback(
    () => dispatch({ type: "REMOVE_DISCOUNT_CODE" }),
    [],
  );
  const applyAdminDiscount = useCallback(
    (
      discountType: "percentage" | "fixed_amount",
      value: number,
      reason: string,
    ) =>
      dispatch({
        type: "APPLY_ADMIN_DISCOUNT",
        discountType,
        value,
        reason,
      }),
    [],
  );
  const removeAdminDiscount = useCallback(
    () => dispatch({ type: "REMOVE_ADMIN_DISCOUNT" }),
    [],
  );
  const setLoading = useCallback(
    (loading: boolean) => dispatch({ type: "SET_LOADING", loading }),
    [],
  );
  const setError = useCallback(
    (error: string | null) => dispatch({ type: "SET_ERROR", error }),
    [],
  );
  const resetFlow = useCallback(() => dispatch({ type: "RESET_FLOW" }), []);

  const value = useMemo<RegistrationContextType>(
    () => ({
      ...state,
      nextStep,
      prevStep,
      goToStep,
      updateContact,
      setAttendees,
      updateAvailableSeats,
      applyVoucher,
      removeVoucher,
      applyDiscountCode,
      removeDiscountCode,
      applyAdminDiscount,
      removeAdminDiscount,
      setLoading,
      setError,
      resetFlow,
    }),
    [
      state,
      nextStep,
      prevStep,
      goToStep,
      updateContact,
      setAttendees,
      updateAvailableSeats,
      applyVoucher,
      removeVoucher,
      applyDiscountCode,
      removeDiscountCode,
      applyAdminDiscount,
      removeAdminDiscount,
      setLoading,
      setError,
      resetFlow,
    ],
  );

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
}

// ---------- Hook ----------

export function useRegistration(): RegistrationContextType {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error(
      "useRegistration must be used within a RegistrationProvider",
    );
  }
  return context;
}
